import { formatLinkedContactDisplay } from "../auth/utils/contactLink";
import { formatCreatedByDisplay as formatCreatedByFromSerializer } from "./simplifiedContactSerializer";

/** Shown when a table cell has no value. */
export const TABLE_EMPTY_CELL = "—";

/** Default max visible characters before ellipsis in table text cells. */
export const DEFAULT_TABLE_TEXT_TRUNCATE_MAX = 42;

/**
 * Truncates plain text for compact table cells (adds ellipsis when over maxLength).
 * @param {unknown} value
 * @param {number} [maxLength]
 * @returns {string} Empty string when no text (caller may substitute TABLE_EMPTY_CELL).
 */
export function truncateTableText(value, maxLength = DEFAULT_TABLE_TEXT_TRUNCATE_MAX) {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}…`;
}

/**
 * Normalizes a user-entered URL for use in href (adds https:// when missing).
 * @param {unknown} raw
 * @returns {string|null}
 */
export function externalLinkHref(raw) {
  if (!raw || typeof raw !== "string") return null;
  const t = raw.trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

/**
 * Display label for a person/contact row (assignee, linked contact, etc.).
 * @param {object|null|undefined} person
 * @param {string} [fallback]
 */
export function formatPersonDisplay(person, fallback = TABLE_EMPTY_CELL) {
  if (!person || typeof person !== "object") return fallback;
  if (typeof person.name === "string" && person.name.trim()) {
    return person.name.trim();
  }
  const linked = formatLinkedContactDisplay(person);
  return linked || fallback;
}

/**
 * Full address string for a property object or task-shaped payload.
 * @param {object|null|undefined} source Property, or task with `property` / `property_address`.
 */
export function formatPropertyFullAddress(source) {
  if (source == null) return TABLE_EMPTY_CELL;

  if (typeof source.full_address === "string" && source.full_address.trim()) {
    return source.full_address.trim();
  }

  if (typeof source.property_address === "string" && source.property_address.trim()) {
    return source.property_address.trim();
  }

  const property =
    source.property != null && typeof source.property === "object" ? source.property : source;

  if (typeof property.address === "string" && property.address.trim()) {
    return property.address.trim();
  }

  const parts = [
    property.street_number,
    property.street_name,
    property.city,
    property.state,
    property.zip_code,
  ].filter((part) => part != null && String(part).trim() !== "");

  return parts.length > 0 ? parts.join(" ") : TABLE_EMPTY_CELL;
}

/**
 * Task status label for table columns.
 * @param {object|null|undefined} task
 */
export function formatTaskStatusDisplay(task) {
  return task?.status === "completed" ? "Completed" : "Not completed";
}

/**
 * Display string for table "Created by" columns (contact on user, not raw user).
 * @param {object|null|undefined} createdBy
 * @param {string} [fallback]
 */
export function formatCreatedByDisplay(createdBy, fallback = "") {
  return formatCreatedByFromSerializer(createdBy, fallback);
}
